// Legacy service and Redux modules are still JavaScript; keep this migration focused on the component boundary.
// @ts-nocheck
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { setGlobalLoader } from "../../../redux/slices/globalLoader";
import GlobalLoader from "./globalLoader";
import { setUserType } from "../../../redux/slices/userType";
import { useDispatch, useSelector } from "react-redux";
import Header from "./header";
import { generateToken, lineVerify } from '../services/order.service';
import {
    getPersistentSessionKey,
    removePersistentSessionKey,
    setPersistentSessionKey,
    setSessionKey,
} from "../../../utils/commonfunction";
import { addQrPanelToken , ChargeSettingsDetail} from "../../../redux/slices/qrcodeDetails";
import { getRestaurantSettings } from "../../restaurant/services/profile.services";
import { ORDER_PLATFORM } from "../../../utils/Constants";
import { cashRegisterData } from "../services/menu.service";
import notFoundImg from '../../../assets/restaurant/images/not-found.svg';
import { useTranslation } from "react-i18next";
import {
    applyEmbeddedViewport,
    postQrNativeMessage,
    QR_NATIVE_ACTIONS,
    stopQrNativeLoader,
    WAITER_SELECT_TABLE_SCREEN,
} from "../utils/nativeBridge";
import { endVisitIfUnused, endVisitOnPagehide } from "../utils/qrVisit";

const QR_CONTEXT_KEY = "qr_context";
const QR_LINE_RETURN_CONTEXT_KEY = "qr_line_return_context";
const QR_AUTH_KEY = "qr_user_auth";
const QR_LINE_AUTH_DEFAULT_ERROR = "LINE login was not completed. Please try again.";
const QR_AUTH_MAX_AGE_MS = 30 * 60 * 1000;
const normalizeLineAuthError = (message) => {
    if (!message) {
        return QR_LINE_AUTH_DEFAULT_ERROR;
    }

    const normalized = String(message).trim();
    if (!normalized) {
        return QR_LINE_AUTH_DEFAULT_ERROR;
    }

    if (
        /invalid authorization code/i.test(normalized) ||
        /invalid.*state/i.test(normalized) ||
        /authorization.*canceled/i.test(normalized) ||
        /access denied/i.test(normalized)
    ) {
        return QR_LINE_AUTH_DEFAULT_ERROR;
    }

    return normalized;
};

const parseStoredQrAuth = () => {
    try {
        const saved = getPersistentSessionKey(QR_AUTH_KEY);
        if (!saved) {
            return null;
        }

        const parsed = JSON.parse(saved);
        if (!parsed || typeof parsed !== "object" || !parsed.token) {
            return null;
        }

        return parsed;
    } catch {
        return null;
    }
};

const QrOrderLayout = () => {
    // Embedded in the waiter PWA's iframe: apply the non-zooming viewport the
    // native app used to inject. No-op outside an embed.
    useEffect(() => {
        applyEmbeddedViewport();
    }, []);

    const dispatch = useDispatch();
    const location = useLocation();
    const loaderState = useSelector((state) => state.globalLoader);
    const tableDetails = useSelector((state) => state.QrOrderToken.tableDetails);
    const queryParams = new URLSearchParams(window.location.search);
    const storedQrContext = (() => {
        try {
            const saved = getPersistentSessionKey(QR_CONTEXT_KEY);
            return saved ? JSON.parse(saved) : null;
        } catch {
            return null;
        }
    })();
    const restaurant_id = queryParams.get('restaurant_id') || storedQrContext?.restaurant_id || null;
    const table_id = queryParams.get('table_id') || storedQrContext?.table_id || null;
    const base = queryParams.get('base') || storedQrContext?.base || null;
    const [token, setToken] = useState(null);
    const [layoutHeight, setLayoutHeight] = useState("100vh");
    const [layoutHeightFlag, setLayoutHeightFlag] = useState(true);
    const [registerData , setRegisterData] = useState(null);
    const [bootstrapError, setBootstrapError] = useState("");
    const [lineAuthChecking, setLineAuthChecking] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return Boolean(params.get("code") && params.get("state"));
    });
    const lineCallbackProcessingRef = useRef(null);
    const qrBootstrapRetriedRef = useRef(false);
    const {t}=useTranslation();
   
    const navigate = useNavigate();

    const getFallbackRegisterState = () => ({
        cashRegisterEntry: base === ORDER_PLATFORM.APP,
        settlement_status: null
    });
    const buildReviewSearchParams = (params, responseData = {}) => {
        const nextParams = new URLSearchParams(params.toString());
        nextParams.delete("code");
        nextParams.delete("state");
        nextParams.delete("error");
        nextParams.delete("error_description");
        nextParams.delete("liffClientId");
        nextParams.delete("liffRedirectUri");
        nextParams.delete("lineAppVersion");
        nextParams.delete("friendship_status_changed");

        if (responseData?.restaurant_id) nextParams.set("restaurant_id", responseData.restaurant_id);
        if (responseData?.table_id) nextParams.set("table_id", responseData.table_id);
        if (!nextParams.get("base")) nextParams.set("base", ORDER_PLATFORM.WEB);

        return nextParams;
    };
    const saveQrAuthRecord = (authToken, nextContext = {}) => {
        if (!authToken) {
            return;
        }

        const payload = {
            token: authToken,
            restaurant_id: nextContext.restaurant_id || restaurant_id || null,
            table_id: nextContext.table_id || table_id || null,
            base: nextContext.base || base || null,
            issued_at: Date.now(),
        };

        setPersistentSessionKey("qr_user_token", authToken);
        setPersistentSessionKey(QR_AUTH_KEY, JSON.stringify(payload));
    };
    const clearQrAuthRecord = () => {
        removePersistentSessionKey("qr_user_token");
        removePersistentSessionKey(QR_AUTH_KEY);
    };
    const hydrateQrToken = (nextToken, nextContext = {}) => {
        if (!nextToken) {
            return null;
        }

        setToken(nextToken);
        setSessionKey("qr_user_token", nextToken);
        saveQrAuthRecord(nextToken, nextContext);
        dispatch(addQrPanelToken(nextToken));
        return nextToken;
    };
    const mintQrToken = (nextContext = {}) => {
        if (nextContext.base === ORDER_PLATFORM.APP) {
            const appToken = queryParams.get("token");
            return Promise.resolve(
                hydrateQrToken(appToken, {
                    restaurant_id: nextContext.restaurant_id,
                    table_id: nextContext.table_id,
                    base: nextContext.base,
                })
            );
        }

        return generateToken({
            restaurant_id: nextContext.restaurant_id,
            table_id: nextContext.table_id,
        }).then((response) => {
            const nextToken = response?.data?.token;
            return hydrateQrToken(nextToken, {
                restaurant_id: nextContext.restaurant_id,
                table_id: nextContext.table_id,
                base: nextContext.base,
            });
        });
    };
    const failBootstrap = () => {
        dispatch(setGlobalLoader(false));
        if (base === ORDER_PLATFORM.APP) {
            stopQrNativeLoader(base);
            postQrNativeMessage(base, QR_NATIVE_ACTIONS.GO_TO, WAITER_SELECT_TABLE_SCREEN);
            return;
        }
        setBootstrapError(t("qr_bootstrap_error"));
    };

    const recoverQrBootstrap = () => {
        if (
            qrBootstrapRetriedRef.current ||
            !restaurant_id ||
            !table_id ||
            base !== ORDER_PLATFORM.WEB
        ) {
            failBootstrap();
            return Promise.resolve(null);
        }

        qrBootstrapRetriedRef.current = true;
        clearQrAuthRecord();
        removePersistentSessionKey("qr_line_auth");
        setRegisterData(null);
        setBootstrapError("");

        return mintQrToken({
            restaurant_id,
            table_id,
            base,
        }).catch((error) => {
            console.log("QR bootstrap token refresh error===>", error);
            failBootstrap();
            return null;
        });
    };
    const handleQrBootstrapFailure = (error, source) => {
        console.log(`${source} error===>`, error);
        if (error?.response?.status === 401) {
            return recoverQrBootstrap();
        }

        failBootstrap();
        return Promise.resolve(null);
    };

    useEffect(() => {
        if (restaurant_id || table_id || base) {
            setPersistentSessionKey(
                QR_CONTEXT_KEY,
                JSON.stringify({
                    restaurant_id,
                    table_id,
                    base,
                })
            );
        }
    }, [restaurant_id, table_id, base]);

    useEffect(() => {
        const handleUnauthorized = (event) => {
            handleQrBootstrapFailure(event?.detail?.error, event?.detail?.source || "QR bootstrap");
        };

        window.addEventListener("qr-bootstrap-unauthorized", handleUnauthorized);
        return () => {
            window.removeEventListener("qr-bootstrap-unauthorized", handleUnauthorized);
        };
    }, [restaurant_id, table_id, base]);

    useEffect(() => {
        const missingQrContext = !queryParams.get("restaurant_id") || !queryParams.get("table_id") || !queryParams.get("base");
        const isQrCheckoutPath = location.pathname.startsWith("/qr-order");

        if (
            isQrCheckoutPath &&
            missingQrContext &&
            (restaurant_id || table_id || base)
        ) {
            const restoredParams = new URLSearchParams(location.search);
            if (restaurant_id) restoredParams.set("restaurant_id", restaurant_id);
            if (table_id) restoredParams.set("table_id", table_id);
            if (base) restoredParams.set("base", base);
            navigate(`${location.pathname}?${restoredParams.toString()}`, { replace: true });
        }
    }, [base, location.pathname, location.search, navigate, restaurant_id, table_id]);

    

    const newToken = useSelector((state)=>{return state.QrOrderToken.qrPanelToken});
    useEffect(()=>{
      setToken(newToken);
    },[newToken]);

    useEffect(() => {
        const body = document.querySelector('body');
        if (body) {
            body.classList.add('restaurant_inner_body');
        }
    }, []);

    useEffect(() => {
        if (!restaurant_id || !table_id || !base) {
            return;
        }

        const storedQrAuth = parseStoredQrAuth();
        const hasExplicitQrParams =
            Boolean(queryParams.get("restaurant_id")) &&
            Boolean(queryParams.get("table_id")) &&
            Boolean(queryParams.get("base"));
        const canReuseStoredAuth =
            !hasExplicitQrParams &&
            storedQrAuth?.token &&
            storedQrAuth?.restaurant_id &&
            storedQrAuth?.table_id &&
            storedQrAuth?.base &&
            String(storedQrAuth.restaurant_id) === String(restaurant_id) &&
            String(storedQrAuth.table_id) === String(table_id) &&
            String(storedQrAuth.base) === String(base) &&
            Date.now() - Number(storedQrAuth.issued_at || 0) < QR_AUTH_MAX_AGE_MS;

        setBootstrapError("");
        qrBootstrapRetriedRef.current = false;

        if (canReuseStoredAuth) {
            hydrateQrToken(storedQrAuth.token, storedQrAuth);
            return;
        }

        if (base === ORDER_PLATFORM.WEB) {
            clearQrAuthRecord();
            mintQrToken({ restaurant_id, table_id, base }).catch((err) => {
                handleQrBootstrapFailure(err, "Generate QR token");
            });
            return;
        }

        hydrateQrToken(queryParams.get('token'), { restaurant_id, table_id, base });
    }, [base, location.search, restaurant_id, table_id]);

    useEffect(() => {
        const currentParams = new URLSearchParams(location.search);
        const code = currentParams.get("code");
        const state = currentParams.get("state");
        const lineError = currentParams.get("error");
        const lineErrorDescription = currentParams.get("error_description");
        const callbackKey = code && state ? `${code}:${state}` : null;

        if (lineError) {
            removePersistentSessionKey("qr_line_auth");
            setPersistentSessionKey(
                "qr_line_auth_error",
                normalizeLineAuthError(lineErrorDescription)
            );
            const nextParams = buildReviewSearchParams(currentParams);
            navigate(`/qr-order/review?${nextParams.toString()}`, { replace: true });
            return;
        }

        if (!code || !state) {
            lineCallbackProcessingRef.current = null;
            setLineAuthChecking(false);
            return;
        }

        if (lineCallbackProcessingRef.current === callbackKey) {
            return;
        }

        lineCallbackProcessingRef.current = callbackKey;

        setLineAuthChecking(true);
        lineVerify({ code, state }, token ? { token } : {})
            .then((response) => {
                const nextParams = buildReviewSearchParams(currentParams, response?.data);
                const returnPath =
                    response?.data?.return_path ||
                    (() => {
                        try {
                            const saved = getPersistentSessionKey(QR_LINE_RETURN_CONTEXT_KEY);
                            return saved ? JSON.parse(saved)?.pathname : "/qr-order/review";
                        } catch {
                            return "/qr-order/review";
                        }
                    })();

                if (response?.success) {
                    const lineAuthData = {
                        ...response.data,
                        auto_open_add_friend:
                            response?.data?.is_friend !== true &&
                            response?.data?.is_blocked !== true &&
                            Boolean(response?.data?.add_friend_url),
                    };
                    setPersistentSessionKey("qr_line_auth", JSON.stringify(lineAuthData));

                    const ensureQrToken = token
                        ? Promise.resolve(token)
                        : generateToken({
                            restaurant_id: response?.data?.restaurant_id,
                            table_id: response?.data?.table_id,
                        }).then((tokenResponse) => {
                            const nextToken = tokenResponse?.data?.token;
                            if (nextToken) {
                                hydrateQrToken(nextToken, {
                                    restaurant_id: response?.data?.restaurant_id,
                                    table_id: response?.data?.table_id,
                                    base: nextParams.get("base") || ORDER_PLATFORM.WEB,
                                });
                            }
                            return nextToken;
                        });

                    return ensureQrToken.then(() => {
                        removePersistentSessionKey(QR_LINE_RETURN_CONTEXT_KEY);
                        navigate(`${returnPath}?${nextParams.toString()}`, { replace: true });
                    });
                } else {
                    removePersistentSessionKey("qr_line_auth");
                    setPersistentSessionKey(
                        "qr_line_auth_error",
                        normalizeLineAuthError(response?.message)
                    );
                }
                removePersistentSessionKey(QR_LINE_RETURN_CONTEXT_KEY);
                navigate(`${returnPath}?${nextParams.toString()}`, { replace: true });
            })
            .catch((error) => {
                removePersistentSessionKey("qr_line_auth");
                setPersistentSessionKey(
                    "qr_line_auth_error",
                    normalizeLineAuthError(
                        error?.response?.data?.message || error?.message
                    )
                );
                const savedReturnContext = (() => {
                    try {
                        const saved = getPersistentSessionKey(QR_LINE_RETURN_CONTEXT_KEY);
                        return saved ? JSON.parse(saved) : null;
                    } catch {
                        return null;
                    }
                })();
                const nextParams = buildReviewSearchParams(currentParams);
                removePersistentSessionKey(QR_LINE_RETURN_CONTEXT_KEY);
                navigate(`${savedReturnContext?.pathname || "/qr-order/review"}?${nextParams.toString()}`, { replace: true });
            })
            .finally(() => {
                setLineAuthChecking(false);
            });
    }, [dispatch, location.search, navigate, token]);

    

    useLayoutEffect(() => {
        dispatch(setUserType("admin"));
        Promise.all([
            import("../../../assets/qrOrder/css/style.css"),
            import("../../../assets/restaurant/css/style.css"),
        ]).then(() => {
            // dispatch(setGlobalLoader(false));
        });
    }, []);
    useEffect(()=>{
        const applyLayoutHeight = () => setLayoutHeight(`${window.innerHeight}px`);
        const timer = setTimeout(applyLayoutHeight, 100);
        window.addEventListener("resize", applyLayoutHeight);
        return () => {
            clearTimeout(timer);
            window.removeEventListener("resize", applyLayoutHeight);
        };
    },[location]);

    useEffect(() => {
        if(token && token.length>30){
            getRestaurantSettings({
                suppressUnauthorizedHandling: true,
                extraHeaders:{Authorization:`Bearer ${token}`}
            })
              .then((response) => {
                if (response?.data) {
                    setBootstrapError("");
                    dispatch(ChargeSettingsDetail(response.data));
                }
              })
              .catch((error) => {
                handleQrBootstrapFailure(error, "Restaurant settings");
              });
        }

      }, [dispatch, token]);

    useEffect(() => {
        if(token){
            cashRegisterData({_id : restaurant_id} , {token: token}).then((response) => {
                if (response?.data) {
                  const { cashRegisterEntry, settlement_status, cash_register_id } = response.data;
                //   console.log(cashRegisterEntry, settlement_status, cash_register_id );

                setRegisterData({
                    cashRegisterEntry : cashRegisterEntry , 
                    settlement_status : settlement_status 
                })
                setBootstrapError("");
                } else {
                    setRegisterData(getFallbackRegisterState());
                }
              })
              .catch((error) => {
                handleQrBootstrapFailure(error, "Cash register details");
              })
              .finally(() => {
                dispatch(setGlobalLoader(false));
              });
        }
        
      }, [token]);

    const visitRef = useRef(null);
    visitRef.current = { restaurant_id, table_id, tableDetails };
    useEffect(() => {
        const closeUnusedVisit = (event) => {
            if (event?.persisted) {
                return;
            }
            const { restaurant_id: rid, table_id: tid, tableDetails: details } = visitRef.current;
            if (event) {
                endVisitOnPagehide(rid, tid, details);
            } else {
                endVisitIfUnused(rid, tid, details);
            }
        };

        window.addEventListener("pagehide", closeUnusedVisit);
        return () => {
            window.removeEventListener("pagehide", closeUnusedVisit);
            closeUnusedVisit();
        };
    }, []);

    useEffect(() => {
        if (base === ORDER_PLATFORM.APP && !loaderState && token) {
            stopQrNativeLoader(base);
        }
    }, [base, loaderState, token]);

    return (
        <>
            {(loaderState && token) || lineAuthChecking ? (
                <GlobalLoader />
            ) : bootstrapError ? (
                <div className='mobile-container'>
                    <div className='mobile-container-wraper d-flex align-items-center justify-content-center px-3' id="qr-wrapper" style={{height:layoutHeight}}>
                        <div className='card text-center py-4 px-3 w-100' style={{maxWidth:"440px"}}>
                            <img src={notFoundImg} alt={t("qr_session_error")} style={{width:"96px"}} className='mx-auto mb-3' />
                            <span className='d-block fs-18 mb-3'>{bootstrapError}</span>
                            <button
                                className='btn btn-primary'
                                onClick={() => {
                                    clearQrAuthRecord();
                                    window.location.replace(window.location.href);
                                }}
                            >
                                {t("label_refresh")}
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className='mobile-container'>
                    <div className='mobile-container-wraper' id="qr-wrapper" style={{height:layoutHeight}}>
                        <Header />
                        {registerData !== null && (
                            registerData?.cashRegisterEntry !== true
                                ? <div className='text-center card py-5 mx-3 px-2 my-auto'>
                                    <span className="d-block fs-20">{t("label_register_close_text")}</span>
                                  </div>
                                : <Outlet />
                        )}


                    </div>
                </div>
            )}
        </>
    );
};
export default QrOrderLayout;
